import React, { useEffect, useMemo, useState } from 'react';
import { exerciseLibrary, workoutExecution, workoutPlans } from '../../services/api';
import { Modal, errorList } from '../ExerciseLibrary/shared';
import '../ExerciseLibrary/ExerciseLibrary.css';

/*
 * Gerador de planos (Treino 3.0). Três passos, sem gravar nada até o treinador decidir:
 *   1. Parâmetros — objetivo, experiência, dias por semana, duração, grupos em foco, o que evitar e (opcional) o sócio.
 *   2. Proposta   — os dias e exercícios, o volume semanal por grupo e os avisos. Dá para ajustar séries/repetições/descanso,
 *                   substituir por uma alternativa curada, retirar, excluir de vez (e gerar outra) ou pedir outra proposta.
 *   3. Usar       — a proposta passa para o editor de planos como rascunho: aí escolhe-se o sócio (ou guarda-se como modelo),
 *                   revê-se e grava-se. Atribuir é sempre uma decisão a seguir a esta.
 *
 * Vocabulário (para não misturar): um PLANO SEMANAL é o conjunto de dias; cada dia é um TREINO PRESCRITO; a SESSÃO EXECUTADA é o
 * que o sócio regista ao treinar (séries, cargas, esforço) e nunca se confunde com o que foi prescrito.
 */

const GOALS = [
  ['general', 'Condição geral'],
  ['hypertrophy', 'Hipertrofia (ganhar massa)'],
  ['strength', 'Força'],
  ['endurance', 'Resistência'],
];
const LEVELS = [['beginner', 'Iniciante'], ['intermediate', 'Intermédio'], ['advanced', 'Avançado']];
const STATUS_TONE = { ok: 'ok', low: 'warn', high: 'warn', missing: 'bad' };
const STATUS_LABEL = { ok: 'Dentro do intervalo', low: 'Abaixo do intervalo', high: 'Acima do intervalo', missing: 'Sem trabalho' };
const SKIP_GROUPS = new Set(['cardio', 'full-body', 'mobility', 'stretching']);

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/** Passa a proposta do servidor para o rascunho do editor de planos (o mesmo formato de `toDraft`). */
export const toStudioDraft = (plan, { userId } = {}) => ({
  planType: userId ? 'MemberPlan' : 'Template',
  userId: userId ? String(userId) : '',
  title: plan.title,
  description: [plan.split ? `Divisão: ${plan.split}.` : null, ...(plan.notes || [])].filter(Boolean).join('\n'),
  notes: '',
  startDate: new Date().toISOString().slice(0, 10),
  endDate: '',
  workoutDays: plan.days.map((day, i) => ({
    dayName: day.name,
    orderIndex: i,
    notes: day.focus || '',
    exercises: day.exercises.map((ex, j) => ({
      name: ex.name,
      exerciseLibraryId: ex.exerciseLibraryId,
      setsTarget: ex.sets,
      repsMin: ex.repsMin,
      repsMax: ex.repsMax,
      restSeconds: ex.restSeconds,
      targetWeightKg: ex.targetWeightKg ?? '',
      tempo: '',
      trackingType: ex.trackingType || 'weight-reps',
      orderIndex: j,
      // O "porquê" do gerador (a regra que escolheu o exercício) viaja na nota do exercício: é o que o atleta
      // lê no player (💬). Antes morria aqui — a razão só existia no diálogo do treinador.
      notes: ex.reason || '',
      videoUrl: '',
      imageUrl: ex.imageUrl || '',
      gifUrl: '',
    })),
  })),
});

function ExerciseRow({ ex, onChange, onRemove, onExclude, onReplace }) {
  const [alts, setAlts] = useState(null);   // null = fechado · [] = sem alternativas
  const [loading, setLoading] = useState(false);

  const openAlternatives = async () => {
    if (alts) { setAlts(null); return; }
    setLoading(true);
    try {
      const { data } = await exerciseLibrary.get(ex.exerciseLibraryId);
      setAlts((data.substitutes || []).filter((s) => s.isActive !== false && s.available));
    } catch {
      setAlts([]);
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, min, max, width = 64) => (
    <label style={{ display: 'inline-flex', flexDirection: 'column', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
      {label}
      <input className="lib-input" style={{ width, minHeight: 36, padding: '4px 8px' }} inputMode="numeric" value={ex[key]}
        aria-label={`${label} de ${ex.name}`} onChange={(e) => onChange({ [key]: e.target.value === '' ? '' : clamp(Math.trunc(Number(e.target.value)) || 0, min, max) })} />
    </label>
  );

  return (
    <li className="lib-sub" style={{ listStyle: 'none', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }} data-testid={`gen-ex-${ex.exerciseLibraryId}`}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{ex.name}</div>
          <div className="lib-hint">{ex.groupLabel}{ex.equipment ? ` · ${ex.equipment}` : ''}</div>
          <div className="lib-hint">{ex.reason}</div>
          {(ex.fromHistory || ex.startingLoad) && ex.targetWeightKg != null && (
            <div className="lib-hint" data-testid={`gen-progression-${ex.exerciseLibraryId}`}>
              {ex.progressionNote || 'Carga sugerida a partir do último treino real do atleta.'}
              {ex.progressed ? ' 📈' : ''}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {field('sets', 'Séries', 1, 10)}
          {field('repsMin', 'Reps mín.', 1, 100)}
          {field('repsMax', 'Reps máx.', 1, 100)}
          {field('restSeconds', 'Descanso (s)', 0, 600, 76)}
        </div>
      </div>
      <div className="lib-chips" style={{ marginTop: 6 }}>
        <button type="button" className="lib-btn small" onClick={openAlternatives} disabled={loading} aria-expanded={!!alts}>Substituir</button>
        <button type="button" className="lib-btn small" onClick={onRemove}>Retirar</button>
        <button type="button" className="lib-btn small danger" onClick={onExclude} title="Nunca incluir este exercício e gerar outra proposta">Excluir e gerar outra</button>
      </div>
      {alts && (
        <div style={{ marginTop: 6 }} role="group" aria-label={`Alternativas a ${ex.name}`}>
          {alts.length === 0
            ? <span className="lib-hint">Este exercício não tem alternativas curadas disponíveis neste ginásio.</span>
            : alts.map((a) => (
              <button key={a.exerciseId} type="button" className="lib-btn small" style={{ marginRight: 6, marginBottom: 6 }}
                onClick={() => { onReplace(a); setAlts(null); }}>{a.name}</button>
            ))}
        </div>
      )}
    </li>
  );
}

export default function PlanGeneratorDialog({ members = [], onClose, onUse }) {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ goal: 'general', experience: 'beginner', daysPerWeek: 3, sessionMinutes: 60, focusGroups: [], userId: '', excludeIds: [], seed: 0, avoidMuscles: [] });
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState([]);
  const [recovery, setRecovery] = useState(null);
  // Enquanto o treinador não tocar na lista, a sugestão segue os FACTOS (músculos já treinados hoje).
  const [avoidTouched, setAvoidTouched] = useState(false);

  useEffect(() => {
    exerciseLibrary.meta().then(({ data }) => setGroups((data.groups || []).filter((g) => !SKIP_GROUPS.has(g.code)))).catch(() => setGroups([]));
  }, []);

  // O estado de recuperação do atleta escolhido: factos (quando foi a última vez, séries nos últimos 7 dias).
  useEffect(() => {
    let alive = true;
    setRecovery(null);
    setAvoidTouched(false);
    if (!form.userId) return () => { alive = false; };
    workoutExecution.muscleRecovery(form.userId)
      .then(({ data }) => { if (alive) setRecovery(data); })
      .catch(() => { if (alive) setRecovery({ muscles: [] }); });
    return () => { alive = false; };
  }, [form.userId]);

  // A pré-seleção é o facto «já treinado hoje» — não é um limiar de fadiga inventado (esse é regra do ginásio).
  useEffect(() => {
    if (!recovery || avoidTouched) return;
    const today = (recovery.muscles || []).filter((m) => m.daysSince === 0).map((m) => m.muscleId);
    setForm((f) => (f.avoidMuscles.length === today.length && today.every((c) => f.avoidMuscles.includes(c)) ? f : { ...f, avoidMuscles: today }));
  }, [recovery, avoidTouched]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleFocus = (code) => set({ focusGroups: form.focusGroups.includes(code) ? form.focusGroups.filter((c) => c !== code) : [...form.focusGroups, code] });
  const toggleAvoid = (muscleId) => {
    setAvoidTouched(true);
    set({ avoidMuscles: form.avoidMuscles.includes(muscleId) ? form.avoidMuscles.filter((c) => c !== muscleId) : [...form.avoidMuscles, muscleId] });
  };

  const generate = async (override = {}) => {
    const f = { ...form, ...override };
    setBusy(true);
    setErrors([]);
    try {
      const { data } = await workoutPlans.generate({
        goal: f.goal,
        experience: f.experience,
        daysPerWeek: Number(f.daysPerWeek),
        sessionMinutes: Number(f.sessionMinutes),
        focusGroups: f.focusGroups,
        avoidMuscles: f.avoidMuscles,
        excludeExerciseIds: f.excludeIds,
        userId: f.userId ? Number(f.userId) : undefined,
        seed: f.seed,
      });
      setForm(f);
      setPlan(data);
    } catch (e) {
      setErrors(errorList(e, 'Não foi possível gerar o plano.'));
    } finally {
      setBusy(false);
    }
  };

  const editDay = (dayIndex, mutate) => setPlan((p) => ({
    ...p,
    days: p.days.map((d, i) => (i === dayIndex ? mutate(d) : d)),
  }));

  const issues = useMemo(() => {
    if (!plan) return [];
    const found = [];
    plan.days.forEach((d) => {
      if (d.exercises.length === 0) found.push(`${d.name} ficou sem exercícios.`);
      d.exercises.forEach((e) => {
        if (e.repsMax !== '' && e.repsMin !== '' && Number(e.repsMax) < Number(e.repsMin)) found.push(`${e.name}: o máximo de repetições é menor do que o mínimo.`);
        if (e.sets === '' || e.repsMin === '' || e.repsMax === '') found.push(`${e.name}: falta séries ou repetições.`);
      });
    });
    return found;
  }, [plan]);

  const total = plan ? plan.days.reduce((n, d) => n + d.exercises.length, 0) : 0;

  return (
    <Modal
      title="Gerar plano de treino"
      onClose={onClose}
      footer={plan ? (
        <>
          <button type="button" className="lib-btn" onClick={() => setPlan(null)} disabled={busy}>← Parâmetros</button>
          <button type="button" className="lib-btn" onClick={() => generate({ seed: form.seed + 1 })} disabled={busy}>Outra proposta</button>
          <button type="button" className="lib-btn primary" disabled={busy || issues.length > 0 || total === 0}
            onClick={() => onUse(toStudioDraft(plan, { userId: form.userId }))}>
            Usar no editor
          </button>
        </>
      ) : (
        <>
          <button type="button" className="lib-btn" onClick={onClose}>Cancelar</button>
          <button type="button" className="lib-btn primary" onClick={() => generate()} disabled={busy}>{busy ? 'A gerar…' : 'Gerar proposta'}</button>
        </>
      )}
    >
      {errors.length > 0 && <div className="lib-notice bad" role="alert"><ul>{errors.map((e) => <li key={e}>{e}</li>)}</ul></div>}

      {!plan && (
        <div data-testid="gen-params">
          <p className="lib-hint" style={{ marginTop: 0 }}>
            Propõe um <strong>plano semanal</strong> (cada dia é um treino prescrito) só com exercícios que o ginásio consegue fazer. Nada é gravado
            até o rever e o guardar; a sessão executada é o que o atleta regista ao treinar.
          </p>
          <div className="lib-row">
            <div className="lib-field">
              <label className="lib-label" htmlFor="gen-goal">Objetivo</label>
              <select id="gen-goal" className="lib-select" value={form.goal} onChange={(e) => set({ goal: e.target.value })}>
                {GOALS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="lib-field">
              <label className="lib-label" htmlFor="gen-level">Experiência</label>
              <select id="gen-level" className="lib-select" value={form.experience} onChange={(e) => set({ experience: e.target.value })}>
                {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="lib-field">
              <label className="lib-label" htmlFor="gen-days">Dias por semana</label>
              <select id="gen-days" className="lib-select" value={form.daysPerWeek} onChange={(e) => set({ daysPerWeek: Number(e.target.value) })}>
                {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="lib-field">
              <label className="lib-label" htmlFor="gen-min">Duração da sessão (min)</label>
              <input id="gen-min" className="lib-input" type="number" min={30} max={120} step={5} value={form.sessionMinutes}
                onChange={(e) => set({ sessionMinutes: e.target.value === '' ? '' : Number(e.target.value) })} />
              <span className="lib-hint">Entre 30 e 120, aquecimento incluído.</span>
            </div>
          </div>

          <div className="lib-field">
            <span className="lib-label">Grupos a reforçar (opcional)</span>
            <div className="lib-chips" role="group" aria-label="Grupos a reforçar">
              {groups.map((g) => (
                <label key={g.code} className="lib-check" style={{ marginRight: 10 }}>
                  <input type="checkbox" checked={form.focusGroups.includes(g.code)} onChange={() => toggleFocus(g.code)} />{g.label}
                </label>
              ))}
            </div>
          </div>

          <div className="lib-field">
            <label className="lib-label" htmlFor="gen-user">Atleta (opcional)</label>
            <select id="gen-user" className="lib-select" value={form.userId} onChange={(e) => set({ userId: e.target.value })}>
              <option value="">Sem atleta — será um modelo</option>
              {members.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <span className="lib-hint">Com atleta, as cargas sugeridas vêm só do histórico real dele; sem histórico não se inventam cargas.</span>
          </div>

          {form.userId && (
            <div className="lib-field" data-testid="gen-recovery">
              <label className="lib-label">Recuperação muscular</label>
              {!recovery && <span className="lib-hint">⏳ A ler o histórico do atleta…</span>}
              {recovery && (recovery.muscles || []).length === 0 && (
                <span className="lib-hint" data-testid="gen-recovery-empty">Sem treinos concluídos: não há nada a sugerir. A escolha do que evitar é tua.</span>
              )}
              {recovery && (recovery.muscles || []).length > 0 && (
                <>
                  <div className="lib-chips" role="group" aria-label="Músculos a deixar de fora">
                    {recovery.muscles.filter((m) => m.daysSince !== null && m.daysSince <= 7).map((m) => (
                      <label key={m.muscleId} className="lib-check" style={{ marginRight: 10 }} data-testid={`recovery-${m.muscleId}`}>
                        <input type="checkbox" checked={form.avoidMuscles.includes(m.muscleId)} onChange={() => toggleAvoid(m.muscleId)} />
                        {m.name} <span className="lib-hint">
                          {m.daysSince === 0 ? 'treinado hoje' : m.daysSince === 1 ? 'treinado ontem' : `há ${m.daysSince} dias`}
                          {m.setsLast7Days > 0 ? ` · ${m.setsLast7Days} séries em 7 dias` : ''}
                        </span>
                      </label>
                    ))}
                  </div>
                  <span className="lib-hint" data-testid="gen-recovery-note">
                    Já vêm marcados os músculos <strong>treinados hoje</strong> (é o que o histórico diz, não um limiar de fadiga).
                    O que fica marcado é deixado de fora desta proposta. Quem define o descanso é o ginásio — aqui não se inventa essa regra.
                  </span>
                </>
              )}
            </div>
          )}

          {form.excludeIds.length > 0 && (
            <div className="lib-notice">
              {form.excludeIds.length} exercício(s) excluído(s) nesta sessão.{' '}
              <button type="button" className="lib-btn small" onClick={() => set({ excludeIds: [] })}>Limpar exclusões</button>
            </div>
          )}
        </div>
      )}

      {plan && (
        <div data-testid="gen-preview">
          <div className="lib-banner" role="status">
            <strong>{plan.title}</strong>
            <span>{plan.split}</span>
            <span>{plan.days.length} dias · {total} exercícios</span>
          </div>

          {plan.warnings?.length > 0 && (
            <div className="lib-notice warn" role="status" data-testid="gen-warnings"><ul>{plan.warnings.map((w) => <li key={w}>{w}</li>)}</ul></div>
          )}
          {issues.length > 0 && (
            <div className="lib-notice bad" role="alert" data-testid="gen-issues"><ul>{issues.map((w) => <li key={w}>{w}</li>)}</ul></div>
          )}

          {plan.days.map((day, dayIndex) => (
            <section key={`${day.name}-${dayIndex}`} className="lib-section" style={{ marginBottom: 16 }} aria-label={day.name}>
              <h3 style={{ margin: '0 0 2px', fontSize: '1.02rem' }}>{day.name}</h3>
              <div className="lib-hint">{day.focus} · ~{day.estimatedMinutes} min · treino prescrito</div>
              <ol style={{ margin: 0, padding: 0 }}>
                {day.exercises.map((ex, exIndex) => (
                  <ExerciseRow
                    key={`${ex.exerciseLibraryId}-${exIndex}`}
                    ex={ex}
                    onChange={(patch) => editDay(dayIndex, (d) => ({ ...d, exercises: d.exercises.map((x, i) => (i === exIndex ? { ...x, ...patch } : x)) }))}
                    onRemove={() => editDay(dayIndex, (d) => ({ ...d, exercises: d.exercises.filter((_, i) => i !== exIndex) }))}
                    onExclude={() => generate({ excludeIds: [...new Set([...form.excludeIds, ex.exerciseLibraryId])] })}
                    onReplace={(alt) => editDay(dayIndex, (d) => ({
                      ...d,
                      exercises: d.exercises.map((x, i) => (i === exIndex
                        ? { ...x, exerciseLibraryId: alt.exerciseId, name: alt.name, imageUrl: alt.imageUrl || null, reason: `Alternativa escolhida a ${x.name}.`, targetWeightKg: null, fromHistory: false }
                        : x)),
                    }))}
                  />
                ))}
              </ol>
            </section>
          ))}

          {plan.volume?.length > 0 && (
            <section aria-label="Volume semanal">
              <h3 style={{ fontSize: '1.02rem' }}>Volume semanal por grupo</h3>
              <div style={{ overflowX: 'auto' }}>
                <table className="lib-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th align="left">Grupo</th><th align="left">Séries/semana</th><th align="left">Frequência</th><th align="left">Intervalo habitual</th><th align="left">Estado</th></tr></thead>
                  <tbody>
                    {plan.volume.map((v) => (
                      <tr key={v.groupCode} data-testid={`gen-volume-${v.groupCode}`}>
                        <td>{v.groupLabel}</td>
                        <td>{v.setsPerWeek}</td>
                        <td>{v.frequency}×</td>
                        <td>{v.minTarget}–{v.maxTarget}</td>
                        <td><span className={`lib-chip ${STATUS_TONE[v.status] || ''}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="lib-hint">O volume mostrado é o da proposta original; se mexer nas séries, confirme-o no plano guardado.</p>
            </section>
          )}

          {plan.notes?.length > 0 && (
            <section aria-label="O que o gerador sabe e não sabe">
              <h3 style={{ fontSize: '1.02rem' }}>Notas</h3>
              <ul style={{ margin: 0, paddingLeft: 20 }}>{plan.notes.map((n) => <li key={n}>{n}</li>)}</ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
